import { useId, useState, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { SuiteHub, APP_ACCENTS, type HubAppCard } from '@vmlmap/shared-ui';

import { getWaitingOnYou } from '@/api/users';
import { useUserStore } from '@/stores/user';
import { useCanOpenAssetLibrary, useCanOpenContentTracker } from '@/hooks/useAppAccess';
import { isFeatureEnabled } from '@/config/featureFlags';
import { formatRelativeTime } from '@/utils/date';
import { useSuiteNotifications } from '@/layouts/suite/useSuiteNotifications';
import { useSuiteMentions } from '@/layouts/suite/useSuiteMentions';

type ActivityTab = 'all' | 'mentions';

/**
 * Mirrors the backend's MENTIONS_UNSEEN_LOOKBACK: with no read state yet, only
 * the last week's mentions count as new. Naive UTC to match the API timestamps.
 */
function unseenLookbackStart(): string {
  return new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 19);
}

function ActivityTabButton({
  id,
  controls,
  active,
  onClick,
  children,
}: {
  id: string;
  controls: string;
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      id={id}
      aria-controls={controls}
      aria-selected={active}
      onClick={onClick}
      className={`flex items-center rounded-md px-2.5 py-1 text-[12px] font-medium transition-colors ${
        active ? 'bg-slate-100 text-slate-800' : 'text-slate-500 hover:text-slate-700'
      }`}
    >
      {children}
    </button>
  );
}

function ActivityRow({
  isUnread,
  title,
  text,
  campaignId,
  createdAt,
  onClick,
}: {
  isUnread: boolean;
  title: string;
  text: string;
  campaignId?: string;
  createdAt: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-start gap-2.5 px-5 py-3 text-left transition-colors hover:bg-slate-50"
    >
      <span
        className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${
          isUnread ? 'bg-[#0014cc]' : 'bg-slate-200'
        }`}
      />
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-medium text-slate-800">{title}</p>
        <p className="mt-0.5 line-clamp-1 text-[12px] text-slate-500">{text}</p>
        <div className="mt-1 flex items-center gap-1.5 text-[10.5px] text-slate-400">
          {campaignId && (
            <>
              <span className="truncate font-medium">{campaignId}</span>
              <span>&middot;</span>
            </>
          )}
          <span className="flex-shrink-0">{formatRelativeTime(createdAt)}</span>
        </div>
      </div>
    </button>
  );
}

function ActivityEmpty({ children }: { children: ReactNode }) {
  return <div className="px-5 py-10 text-center text-[13px] text-slate-400">{children}</div>;
}

/** Returns a time-of-day greeting for the hub header. */
function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

/**
 * Suite Home — the landing hub across the MAP suite.
 *
 * Cards are per-user, not per-deployment: each app appears only for people
 * holding its grant, so a user granted one app never sees a door into the
 * other. A user granted none falls through to {@link SuiteHub}'s empty state.
 */
export function SuiteHomePage() {
  const navigate = useNavigate();
  const profile = useUserStore((s) => s.profile);
  const { allowed: canOpenAssetLibrary } = useCanOpenAssetLibrary();
  const { allowed: canOpenContentTracker } = useCanOpenContentTracker();
  // The activity feed is Content Tracker campaign data (events, comments), so
  // it's only fetched — and the API only serves it — for users holding that grant.
  const { notifications, lastSeenAt, clickNotification } = useSuiteNotifications({
    enabled: canOpenContentTracker,
  });
  const mentions = useSuiteMentions({ enabled: canOpenContentTracker });
  const tabIds = useId();
  const [tab, setTab] = useState<ActivityTab>('all');
  // Read state as it was when the Mentions tab opened, so the dots on what was
  // new survive the mark-seen refetch for as long as the user is looking.
  const [mentionsBaseline, setMentionsBaseline] = useState<string | null>(null);
  // Counted against the snapshot, not live unread_count, which drops to 0 as
  // soon as mark-seen lands.
  const unreadShown =
    mentionsBaseline === null
      ? 0
      : mentions.mentions.filter((m) => m.created_at > mentionsBaseline).length;

  const { data: waitingOnYouData } = useQuery({
    queryKey: ['waiting-on-you'],
    queryFn: getWaitingOnYou,
    enabled: canOpenContentTracker,
    staleTime: 60_000,
  });
  const waitingOnYou = waitingOnYouData?.count ?? null;

  const firstName = profile?.display_name?.split(' ')[0] ?? 'there';

  const openTab = (next: ActivityTab) => {
    if (next === 'mentions' && tab !== 'mentions') {
      setMentionsBaseline(mentions.lastSeenAt ?? unseenLookbackStart());
      mentions.markSeen();
    }
    setTab(next);
  };

  const cards: HubAppCard[] = [
    // Offered only to users holding the `content_tracker` grant, which the
    // Content Tracker API now enforces: without it every campaign, dashboard
    // and MAP call returns 403, so the card would open a page of errors.
    ...(canOpenContentTracker
      ? [
          {
            app: {
              id: 'tracker',
              name: 'Content Tracker',
              accent: APP_ACCENTS.tracker,
              iconLabel: 'CT',
              description:
                'Track social & search campaigns from MAP import through validation, preview and activation.',
              navItems: [],
            },
            // Zero-count chips are hidden: the card should only speak up when
            // something needs the user.
            stats: [
              ...(waitingOnYou ? [{ label: 'waiting on you', value: waitingOnYou }] : []),
              ...(mentions.unreadCount
                ? [
                    {
                      label: mentions.unreadCount === 1 ? 'new mention' : 'new mentions',
                      value: mentions.unreadCount,
                    },
                  ]
                : []),
            ],
            onOpen: () => navigate({ to: '/content-tracker' }),
          } satisfies HubAppCard,
        ]
      : []),
    // Gated on the same `suiteShell` flag as the /assets route itself, so the
    // card never becomes a dead link when the flag is off, and on the user's
    // own `asset_library` grant, so it isn't offered to people the Asset
    // Library API would turn away.
    ...(isFeatureEnabled('suiteShell') && canOpenAssetLibrary
      ? [
          {
            app: {
              id: 'asset',
              name: 'Asset Library',
              accent: APP_ACCENTS.asset,
              iconLabel: 'AL',
              description: 'Upload, tag and publish Ford assets for reuse across campaigns.',
              navItems: [],
            },
            // No stats yet: Asset Library has no "needs you" signal to show.
            onOpen: () => navigate({ to: '/assets' }),
          },
        ]
      : []),
  ];

  const panelId = `${tabIds}-panel`;
  const tabId = (t: ActivityTab) => `${tabIds}-tab-${t}`;

  // Without the CT grant there is no feed to show; SuiteHub renders its own
  // empty state for an undefined `activity`.
  const activity = canOpenContentTracker ? (
    <>
      <div role="tablist" aria-label="Activity" className="flex gap-1 px-5 pb-1 pt-1">
        <ActivityTabButton
          id={tabId('all')}
          controls={panelId}
          active={tab === 'all'}
          onClick={() => openTab('all')}
        >
          All
        </ActivityTabButton>
        <ActivityTabButton
          id={tabId('mentions')}
          controls={panelId}
          active={tab === 'mentions'}
          onClick={() => openTab('mentions')}
        >
          Mentions
          {mentions.unreadCount > 0 && (
            <span className="ml-1.5 rounded-full bg-[#0014cc] px-1.5 text-[10px] font-semibold text-white">
              {mentions.unreadCount}
            </span>
          )}
        </ActivityTabButton>
      </div>

      <div role="tabpanel" id={panelId} aria-labelledby={tabId(tab)}>
        {tab === 'all' ? (
          notifications.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {notifications.slice(0, 8).map((n) => (
                <ActivityRow
                  key={n.id}
                  isUnread={lastSeenAt ? n.created_at > lastSeenAt : true}
                  title={n.title}
                  text={n.item_text}
                  campaignId={n.campaign_id}
                  createdAt={n.created_at}
                  onClick={() => clickNotification(n)}
                />
              ))}
            </div>
          ) : (
            <ActivityEmpty>No recent activity yet.</ActivityEmpty>
          )
        ) : mentions.mentions.length > 0 ? (
          // Opening this tab marks every mention seen, so every unread one has
          // to be on screen — the list grows (and scrolls) past the usual 8.
          <div className="max-h-[480px] divide-y divide-slate-100 overflow-y-auto">
            {mentions.mentions.slice(0, Math.max(8, unreadShown)).map((m) => (
              <ActivityRow
                key={m.id}
                isUnread={mentionsBaseline !== null && m.created_at > mentionsBaseline}
                title={`${m.author_name} mentioned you`}
                text={m.comment_text}
                campaignId={m.campaign_id}
                createdAt={m.created_at}
                onClick={() => mentions.clickMention(m)}
              />
            ))}
          </div>
        ) : (
          <ActivityEmpty>No one has mentioned you yet.</ActivityEmpty>
        )}
      </div>
    </>
  ) : undefined;

  return (
    <SuiteHub
      title={`${greeting()}, ${firstName}`}
      subtitle="Everything across the MAP suite, in one place."
      cards={cards}
      activity={activity}
    />
  );
}
